import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Calculator, ArrowRight } from "lucide-react";

const currency = (n) =>
  n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });

const PRESETS = [
  { label: "Personal", amount: 25000, rate: 7.9, term: 36 },
  { label: "Business", amount: 150000, rate: 8.5, term: 60 },
  { label: "Signature", amount: 500000, rate: 6.9, term: 84 },
];

export default function CalculatorSection() {
  const [amount, setAmount] = useState(50000);
  const [rate, setRate] = useState(7.9);
  const [term, setTerm] = useState(36);

  const { monthly, totalPaid, totalInterest } = useMemo(() => {
    const r = rate / 100 / 12;
    const n = term;
    const m = r === 0 ? amount / n : (amount * r) / (1 - Math.pow(1 + r, -n));
    const total = m * n;
    return {
      monthly: Number.isFinite(m) ? m : 0,
      totalPaid: Number.isFinite(total) ? total : 0,
      totalInterest: Number.isFinite(total) ? total - amount : 0,
    };
  }, [amount, rate, term]);

  return (
    <section
      id="calculator"
      data-testid="calculator-section"
      className="relative overflow-hidden border-y border-white/10 bg-[#050A1F] py-24 lg:py-32"
    >
      <div className="pointer-events-none absolute inset-0 opacity-[0.07]">
        <div className="absolute -left-32 top-20 h-80 w-80 rounded-full bg-[#D4AF37] blur-[120px]" />
        <div className="absolute -right-32 bottom-10 h-80 w-80 rounded-full bg-[#D4AF37] blur-[120px]" />
      </div>

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid gap-16 lg:grid-cols-5 lg:gap-20">
          <div className="lg:col-span-2">
            <span className="text-xs uppercase tracking-[0.42em] text-[#D4AF37]">
              Instant Estimate
            </span>
            <h2 className="mt-5 font-serif text-4xl font-light leading-tight text-white sm:text-5xl">
              Preview your monthly<br />
              <span className="text-[#D4AF37]">commitment.</span>
            </h2>
            <p className="mt-6 max-w-md text-sm leading-relaxed text-white/60">
              Our calculator is indicative — final terms are tailored to your
              profile by a dedicated underwriter. No credit pull. No obligation.
            </p>

            <div className="mt-10 flex flex-wrap gap-3">
              {PRESETS.map((p) => (
                <button
                  key={p.label}
                  type="button"
                  data-testid={`calc-preset-${p.label.toLowerCase()}`}
                  onClick={() => {
                    setAmount(p.amount);
                    setRate(p.rate);
                    setTerm(p.term);
                  }}
                  className="border border-white/15 bg-white/[0.02] px-4 py-2 text-[11px] uppercase tracking-[0.28em] text-white/70 transition hover:border-[#D4AF37] hover:text-[#D4AF37]"
                >
                  {p.label}
                </button>
              ))}
            </div>

            <Link
              to="/apply"
              data-testid="calc-apply-cta"
              className="mt-10 inline-flex items-center gap-3 border border-[#D4AF37] bg-[#D4AF37] px-6 py-3 text-xs uppercase tracking-[0.28em] text-[#050A1F] transition hover:bg-[#F3E5AB]"
            >
              Secure these terms
              <ArrowRight className="h-4 w-4" strokeWidth={1.6} />
            </Link>
          </div>

          <div className="lg:col-span-3">
            <div className="relative border border-white/10 bg-gradient-to-b from-white/[0.04] to-transparent p-8 backdrop-blur-sm lg:p-12">
              <div className="absolute left-0 top-0 flex items-center gap-3 border-b border-r border-white/10 bg-[#0A1128] px-4 py-2">
                <Calculator className="h-3.5 w-3.5 text-[#D4AF37]" strokeWidth={1.6} />
                <span className="text-[10px] uppercase tracking-[0.42em] text-white/60">
                  Rate · EMI Calculator
                </span>
              </div>

              <div className="mt-10 space-y-8">
                <SliderField
                  label="Loan Amount"
                  value={amount}
                  min={5000}
                  max={1000000}
                  step={1000}
                  display={currency(amount)}
                  onChange={setAmount}
                  testid="calc-amount"
                />
                <SliderField
                  label="Interest Rate"
                  value={rate}
                  min={3}
                  max={18}
                  step={0.1}
                  display={`${rate.toFixed(1)}% APR`}
                  onChange={(v) => setRate(Number(v.toFixed(1)))}
                  testid="calc-rate"
                />
                <SliderField
                  label="Term"
                  value={term}
                  min={6}
                  max={120}
                  step={6}
                  display={`${term} months`}
                  onChange={setTerm}
                  testid="calc-term"
                />
              </div>

              <div className="mt-10 border-t border-white/10 pt-8">
                <div className="grid gap-6 sm:grid-cols-3">
                  <ResultCard
                    label="Monthly"
                    value={currency(monthly)}
                    accent
                    testid="calc-result-monthly"
                  />
                  <ResultCard
                    label="Total Interest"
                    value={currency(totalInterest)}
                    testid="calc-result-interest"
                  />
                  <ResultCard
                    label="Total Payback"
                    value={currency(totalPaid)}
                    testid="calc-result-total"
                  />
                </div>
                <p className="mt-6 text-[11px] uppercase tracking-[0.28em] text-white/40">
                  Estimates only · APR varies by profile & risk assessment
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function SliderField({ label, value, min, max, step, display, onChange, testid }) {
  return (
    <div>
      <div className="mb-3 flex items-baseline justify-between">
        <label className="text-[11px] uppercase tracking-[0.42em] text-white/60">
          {label}
        </label>
        <span
          className="font-serif text-2xl font-light text-[#D4AF37]"
          data-testid={`${testid}-display`}
        >
          {display}
        </span>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        data-testid={`${testid}-slider`}
        style={{ "--pct": `${((value - min) / (max - min)) * 100}%` }}
        className="llu-slider w-full"
      />
    </div>
  );
}

function ResultCard({ label, value, accent, testid }) {
  return (
    <div
      data-testid={testid}
      className={`border p-5 ${
        accent
          ? "border-[#D4AF37]/50 bg-[#D4AF37]/[0.06]"
          : "border-white/10 bg-white/[0.02]"
      }`}
    >
      <p className="text-[10px] uppercase tracking-[0.42em] text-white/50">
        {label}
      </p>
      <p
        className={`mt-3 font-serif text-2xl font-light ${
          accent ? "text-[#D4AF37]" : "text-white"
        }`}
      >
        {value}
      </p>
    </div>
  );
}
